/* eslint-disable no-shadow */

import { ParametersType } from '../constants/telosair-interfaces';

/* eslint-disable jsdoc/require-jsdoc */
export type ConversionRates = {
  [unit: string]: {
    [toUnit: string]: (value: number) => number;
  };
};

export const PARAMETER_CONVERSION: {
  [key in ParametersType]: (value: number, toUnit: string) => number;
} = {
  aqi(value: number) {
    return value;
  },
  ci(value: number) {
    return value;
  },
  pm100_m(value: number) {
    return value;
  },
  pm25_m(value: number) {
    return value;
  },
  co(value: number) {
    return value;
  },
  no2(value: number) {
    return value;
  },
  o3(value: number) {
    return value;
  },
  so2(value: number) {
    return value;
  },
  ch4(value: number) {
    return value;
  },
  hum(value: number) {
    return value;
  },
  /**
   * Converts Temperature values to other units.
   * @param value - The value to convert.
   * @param toUnit - The unit to convert to.
   * @returns - The converted value.
   */
  temp(value: number, toUnit: string): number {
    const conversionRates: ConversionRates = {
      '℃': {
        '°F': (value: number) => (value * 9) / 5 + 32
      },
      '°F': {
        '℃': (value: number) => ((value - 32) * 5) / 9
      }
    };
    return conversionRates['℃'][toUnit] ? conversionRates['℃'][toUnit](value) : value;
  },

  /**
   * Converts Pressure values to other units.
   * @param value - The value to convert.
   * @param toUnit - The unit to convert to.
   * @returns - The converted value.
   */
  pressure(value: number, toUnit: string): number {
    const conversionRates: ConversionRates = {
      '"H2O': {
        atm: (value: number) => value * 0.002458,
        kPa: (value: number) => value * 0.24884
      },
      atm: {
        '"H2O': (value: number) => value / 0.002458,
        kPa: (value: number) => value * 101.325
      },
      kPa: {
        '"H2O': (value: number) => value / 0.24884,
        atm: (value: number) => value / 101.325
      }
    };
    return conversionRates['"H2O'][toUnit] ? conversionRates['"H2O'][toUnit](value) : value;
  },

  /**
   * Converts CO2 values to other units.
   * @param value - The value to convert.
   * @param toUnit - The unit to convert to.
   * @returns - The converted value.
   */
  co2(value: number, toUnit: string): number {
    const conversionRates: ConversionRates = {
      ppm: {
        ppb: (value: number) => value * 1000
      },
      ppb: {
        ppm: (value: number) => value / 1000
      }
    };
    return conversionRates.ppm[toUnit] ? conversionRates.ppm[toUnit](value) : value;
  },

  /**
   * Converts VOCs values to other units.
   * @param value - The value to convert.
   * @param toUnit - The unit to convert to.
   * @returns - The converted value.
   */
  tvoc(value: number, toUnit: string): number {
    const conversionRates: ConversionRates = {
      ppb: {
        ppm: (value: number) => value / 1000
      },
      ppm: {
        ppb: (value: number) => value * 1000
      }
    };
    return conversionRates.ppb[toUnit] ? conversionRates.ppb[toUnit](value) : value;
  },

  /**
   * Converts Particle Concentration values to other units.
   * @param value - The value to convert.
   * @param toUnit - The unit to convert to.
   * @returns - The converted value.
   */
  pn03_m(value: number, toUnit: string): number {
    // Backend normalises all PN values to #/cc (#/cm3) before sending.
    // #/0.1L = 100 × #/cc  →  ×100 to go back
    // #/L    = 1000 × #/cc →  ×1000 to go back
    const conversionRates: ConversionRates = {
      '#/cm3': {
        '#/0.1L': (value: number) => value * 100,
        '#/L': (value: number) => value * 1000
      }
    };
    return conversionRates['#/cm3'][toUnit] ? conversionRates['#/cm3'][toUnit](value) : value;
  }
};
